/**
 * Profile Screen — scrollable profile with sticky tabs.
 * Header scrolls up, tabs stick at top, content scrolls below.
 * Hamburger + notification icons in header area (not separate bar).
 * Desktop: 2x wider content. Mobile: full-width.
 */

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Menu, ArrowLeft, Bell, MoreVertical, ShieldBan } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@turnifly/shared/stores'
import { useProfile } from '@turnifly/shared/hooks'
import { useCombinedUnreadCount } from '@turnifly/shared/hooks'
import { ProfileHeader } from './profile-header'
import { ProfileAboutSection } from './profile-about-section'
import { ProfileStatsTab } from './profile-stats-tab'
import { SettingsDrawer } from './settings-drawer'
import { EditProfileSheet } from './edit-profile-sheet'
import { PrivateLockScreen } from './private-lock-screen'
import { useFollowStatus, useToggleFollow } from '@turnifly/shared/hooks'
import { useBlockUser } from '@turnifly/shared/hooks'
import type { LockScreenState } from './private-lock-screen'
import { ProfileLentaTab } from './profile-lenta-tab'
import { LoadingSpinner } from '@turnifly/shared/ui'

type ProfileSubTab = 'stats' | 'lenta'

export const ProfileScreen: React.FC = () => {
  const { id: paramId } = useParams<{ id: string }>()
  const { user: authUser } = useAuthStore()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)

  const targetUserId = paramId ?? authUser?.id
  const isOwnProfile = !paramId || paramId === authUser?.id

  const [searchParams, setSearchParams] = useSearchParams()
  const highlightPostId = searchParams.get('post')

  // If deep-linked to a specific post, open lenta tab
  const [activeSubTab, setActiveSubTab] = useState<ProfileSubTab>(highlightPostId ? 'lenta' : 'stats')
  const [isSettingsOpen, setSettingsOpen] = useState(false)
  const [isEditOpen, setEditOpen] = useState(false)
  const [isTabsSticky, setTabsSticky] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)

  // Tab swipe
  const profileTouchRef = useRef<{ x: number; y: number } | null>(null)
  const PROFILE_TABS: ProfileSubTab[] = ['stats', 'lenta']

  const handleProfileTouchStart = useCallback((e: React.TouchEvent): void => {
    profileTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }, [])

  const handleProfileTouchEnd = useCallback((e: React.TouchEvent): void => {
    if (!profileTouchRef.current) return
    const dx = e.changedTouches[0].clientX - profileTouchRef.current.x
    const dy = e.changedTouches[0].clientY - profileTouchRef.current.y
    profileTouchRef.current = null
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return
    const idx = PROFILE_TABS.indexOf(activeSubTab)
    const nextIdx = dx < 0 ? idx + 1 : idx - 1
    if (nextIdx >= 0 && nextIdx < PROFILE_TABS.length) {
      setActiveSubTab(PROFILE_TABS[nextIdx])
    }
  }, [activeSubTab])

  const blockMutation = useBlockUser()

  const { data: profile, isLoading, isError } = useProfile(targetUserId)
  const { data: followStatus } = useFollowStatus(
    authUser?.id,
    isOwnProfile ? undefined : targetUserId,
  )
  const { data: unreadData } = useCombinedUnreadCount(isOwnProfile ? authUser?.id : undefined)

  const followMutation = useToggleFollow()

  /**
   * Lock screen state — derived from followStatus + block state.
   * Returns null when content is visible (own profile, public, or
   * accepted follower). Server-side enforcement (RLS + safe RPCs)
   * is the source of truth; this only drives the UI.
   */
  const lockState = useMemo((): LockScreenState | null => {
    if (isOwnProfile || !profile) return null
    if (followStatus?.isBlockedByTarget) return 'blocked'
    if (!profile.is_private) return null
    if (followStatus?.followStatus === 'accepted') return null
    if (followStatus?.followStatus === 'pending') return 'pending'
    return 'private'
  }, [isOwnProfile, profile, followStatus])

  const handleSendFollowRequest = useCallback((): void => {
    if (!authUser?.id || !targetUserId || !profile) return
    followMutation.mutate({
      followerId: authUser.id,
      followingId: targetUserId,
      isTargetPrivate: profile.is_private,
      isCurrentlyFollowing: false,
    })
  }, [authUser?.id, targetUserId, profile, followMutation])

  const subTabs: { id: ProfileSubTab; label: string }[] = useMemo(() => [
    { id: 'stats', label: t('profile.stats') },
    { id: 'lenta', label: t('profile.lenta') },
  ], [t])

  // Sticky tabs on scroll (throttled with rAF)
  const rafRef = useRef<number>(0)
  const handleScroll = useCallback((): void => {
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => {
      if (tabsRef.current && scrollRef.current) {
        const tabsTop = tabsRef.current.offsetTop
        const scrollTop = scrollRef.current.scrollTop
        setTabsSticky(scrollTop >= tabsTop - 56)
      }
      rafRef.current = 0
    })
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', handleScroll)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [handleScroll])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isError || !profile) {
    return (
      <div className="flex items-center justify-center h-full text-text-hint">
        {t('common.error')}
      </div>
    )
  }

  return (
    <div className="relative h-[100dvh]">
      {/* ── Sticky tabs bar (appears on scroll) ── */}
      {isTabsSticky && (
        <div className="fixed top-0 left-0 right-0 z-40 tg-header-safe-area bg-bg/90 backdrop-blur-xl border-b border-white/[0.04]">
          <div className="max-w-2xl mx-auto flex justify-center py-2">
            <TabPills
              subTabs={subTabs}
              activeSubTab={activeSubTab}
              onTabChange={setActiveSubTab}
            />
          </div>
        </div>
      )}

      {/* ── Scrollable content ── */}
      <div
        ref={scrollRef}
        className="h-full overflow-y-auto overscroll-y-contain"
        onTouchStart={handleProfileTouchStart}
        onTouchEnd={handleProfileTouchEnd}
      >
        <div className="max-w-2xl mx-auto pb-32 tg-header-safe-area">
          {/* Icon bar — hamburger/back + notification */}
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <div className="w-10">
              {!isOwnProfile && (
                <button
                  onClick={() => navigate(-1)}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] transition-colors tap-highlight-transparent"
                >
                  <ArrowLeft size={20} className="text-text" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1">
              {isOwnProfile ? (
                <>
                  <button
                    onClick={() => navigate('/inbox')}
                    className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] transition-colors tap-highlight-transparent"
                  >
                    <Bell size={20} className="text-text" />
                    {(unreadData?.total ?? 0) > 0 && (
                      <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-danger flex items-center justify-center">
                        <span className="text-[8px] font-bold text-white">
                          {(unreadData?.total ?? 0) > 9 ? '9+' : unreadData?.total}
                        </span>
                      </div>
                    )}
                  </button>
                  <button
                    onClick={() => setSettingsOpen(true)}
                    className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] transition-colors tap-highlight-transparent"
                  >
                    <Menu size={20} className="text-text" />
                  </button>
                </>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] transition-colors tap-highlight-transparent"
                  >
                    <MoreVertical size={20} className="text-text" />
                  </button>
                  {showUserMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                      <div className="absolute right-0 top-11 z-50 w-48 rounded-xl bg-bg border border-white/[0.08] shadow-glass overflow-hidden">
                        <button
                          onClick={() => {
                            if (!authUser?.id || !targetUserId) return
                            blockMutation.mutate({
                              blockerId: authUser.id,
                              blockedId: targetUserId,
                              isBlocked: false,
                            })
                            setShowUserMenu(false)
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-danger hover:bg-white/[0.04] transition-colors"
                        >
                          <ShieldBan size={16} />
                          {t('profile.blockUser')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Profile Header */}
          <ProfileHeader
            profile={profile}
            isOwnProfile={isOwnProfile}
            followStatus={followStatus}
            onEditProfile={() => setEditOpen(true)}
          />

          {/* About section — visible on other users' profiles */}
          <ProfileAboutSection profile={profile} isOwnProfile={isOwnProfile} />

          {/* Private Profile Lock */}
          {lockState ? (
            <PrivateLockScreen
              state={lockState}
              onSendRequest={handleSendFollowRequest}
              isRequestPending={followMutation.isPending}
            />
          ) : (
            <>
              {/* ── Sub-tabs anchor point ── */}
              <div ref={tabsRef} className="flex justify-center mt-5 mb-3 px-4">
                <TabPills
                  subTabs={subTabs}
                  activeSubTab={activeSubTab}
                  onTabChange={setActiveSubTab}
                />
              </div>

              {/* Tab Content */}
              <div className="px-4">
                {activeSubTab === 'stats' && (
                  <ProfileStatsTab userId={targetUserId!} />
                )}
                {activeSubTab === 'lenta' && (
                  <ProfileLentaTab
                    userId={targetUserId!}
                    highlightPostId={highlightPostId ?? undefined}
                    onHighlightHandled={() => setSearchParams({}, { replace: true })}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Settings Drawer & Edit Sheet */}
      {isOwnProfile && (
        <>
          <SettingsDrawer
            isOpen={isSettingsOpen}
            onClose={() => setSettingsOpen(false)}
          />
          <EditProfileSheet
            isOpen={isEditOpen}
            onClose={() => setEditOpen(false)}
            profile={profile}
          />
        </>
      )}
    </div>
  )
}

// ── Tab Pills (reused in scroll position and sticky bar) ────

interface TabPillsProps {
  subTabs: { id: ProfileSubTab; label: string }[]
  activeSubTab: ProfileSubTab
  onTabChange: (tab: ProfileSubTab) => void
}

const TabPills: React.FC<TabPillsProps> = ({ subTabs, activeSubTab, onTabChange }) => (
  <div className="inline-flex items-center gap-0.5 p-1 rounded-full bg-white/[0.04] backdrop-blur-xl border border-white/[0.06]">
    {subTabs.map((tab) => {
      const isActive = activeSubTab === tab.id
      return (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`relative px-6 py-2 rounded-full whitespace-nowrap text-[13px] font-semibold transition-colors tap-highlight-transparent ${
            isActive ? 'text-text' : 'text-text-hint/50 hover:text-text-hint'
          }`}
        >
          {isActive && (
            <motion.div
              layoutId="profileTabPill"
              className="absolute inset-0 bg-white/[0.08] rounded-full border border-white/[0.06]"
              transition={{ type: 'spring', stiffness: 350, damping: 28 }}
            />
          )}
          <span className="relative z-10">{tab.label}</span>
        </button>
      )
    })}
  </div>
)
